<template>
    <ds-modal
        is-open
        size="xl"
        class="kmn-settings-modal"
        show-close
        @close="close"
    >
        <template #title>
            <div class="header-text">
                <div class="settings-modal-title">
                    {{ headerText }}
                </div>
            </div>
        </template>
        <div class="tab-select">
            <section class="tabs">
                <div class="tab-items">
                    <ds-list-item
                        :class="['tab-item', { selected: isTabSelected(1)} ]"
                        data-qa="number-billing-tab"
                        :title="pricingTabText"
                        @click="handleTabClick(1)"
                    />
                    <ds-list-item
                        :class="['tab-item', { selected: isTabSelected(2)} ]"
                        data-qa="opt-in-tab"
                        :title="optInTitle"
                        @click="handleTabClick(2)"
                    />
                    <ds-list-item
                        v-if="displayAutoReply"
                        :class="['tab-item', { selected: isTabSelected(3)} ]"
                        data-qa="auto-reply-tab"
                        :title="autoNavText"
                        @click="handleTabClick(3)"
                    />
                </div>
            </section>

            <section class="content-section">
                <div v-if="activeTab === BILLING_TAB" class="number-billing">
                    <div class="settings-modal-title">
                        {{ numNavHeader }}
                    </div>
                    <div class="marketing-number">
                        {{ formattedFromNumber }}
                    </div>
                    <div class="description">
                        {{ numNavInfoMsg }}
                    </div>

                    <ds-inline-alert class="compliance-alert" type="warning">
                        <div class="compliance-title">
                            <ds-icon class="alert-icon" name="alert-triangle" />
                            <span class="alert-title">
                                {{ numNavWarnMsg }}
                            </span>
                        </div>

                        <ds-link
                            trailing-icon="external-link"
                            :href="complianceLink"
                            external
                            class="compliance-link"
                        >
                            {{ numNavRuleOne }}
                        </ds-link>

                        <span class="link-source">{{ voiceAccountType }}</span>

                        <ds-link
                            trailing-icon="external-link"
                            :href="bestPracticesLink"
                            external
                            class="compliance-link"
                        >
                            {{ numNavRuleTwo }}
                        </ds-link>

                        <span class="link-source">{{ numNavCullalarTxt }}</span>

                        <ds-link
                            trailing-icon="external-link"
                            :href="tcpaLink"
                            external
                            class="compliance-link"
                        >
                            {{ numNavRuleThree }}
                        </ds-link>

                        <span class="link-source">{{ numNavTcpaText }}</span>
                    </ds-inline-alert>

                    <sms-text-usage />

                    <h4 class="pricing-header">
                        {{ pricing }}
                    </h4>

                    <h6 class="pricing-title">
                        {{ numNavHelpMsgLink }}
                    </h6>
                    <span v-if="isBillingChangesEnabled" class="cost-per-message">
                        {{ costPerMessage }}
                    </span>
                    <span v-else class="pricing-description">
                        {{ pricingDescriptionText }}
                    </span>
                </div>

                <div v-if="activeTab === OPTIN_TAB" class="optin-section">
                    <div class="settings-modal-title">
                        {{ optInTitle }}
                    </div>

                    <p class="description">
                        {{ optInText }}
                    </p>

                    <ds-text-area-field
                        ref="optin"
                        v-model="optInIntro"
                        :label="optNavCustomMsg"
                        class="message-input"
                        data-qa="opt-in-message-intro"
                        :invalid="hasOptinError"
                        submitted
                        :maxlength="SETTINGS_MAX_CHARACTER_COUNT - requiredInfo.length + 1"
                        required
                        auto-grow
                        @input="saveOptInMessage"
                    />

                    <ds-text-area-field
                        :value="requiredInfo.trim()"
                        :label="optNavRequiredMsg"
                        class="required-info"
                        readonly
                        auto-grow
                    >
                        <template #help>
                            <div data-qa="optin-intro-help" class="character-count" :class="{ 'error': hasOptinError }">
                                <DsStatus
                                    v-if="!loadingOptin && !hasOptinError"
                                    class="input-status"
                                    emphasis="medium"
                                    type="good"
                                >
                                    {{ optNavSavedButton }}
                                </DsStatus>
                                <DsSpinner v-else-if="!hasOptinError" class="input-status" :size="12" />
                                {{ optInCharacterCount }}
                            </div>
                        </template>
                    </ds-text-area-field>

                    <ds-inline-alert
                        v-if="hasOptinError"
                        leading-icon
                        class="character-alert"
                        type="critical"
                        data-qa="character-count-alert"
                    >
                        {{ lawCustomMsg }}
                    </ds-inline-alert>

                    <sms-invalid-character-alert
                        v-if="isOptinMessageInvalid"
                        leading-icon
                        class="character-alert"
                        data-qa="encoding-alert"
                    />

                    <ds-inline-alert
                        v-if="showComplianceTip"
                        type="warning"
                        leading-icon
                        class="settings-alert"
                        data-qa="company-name-tip"
                    >
                        You can customize this message, but the law requires you to identify yourself or your business.
                    </ds-inline-alert>

                    <div class="reply-tip-container">
                        <span class="subtitle">
                            {{ optNavRecommendedMsg }}
                        </span>
                    </div>

                    <p class="no-reply-tip">
                        {{ optNavNotificationMsg }}
                    </p>

                    <div class="tip-card">
                        <div>
                            <div class="alert-title">
                                {{ optNavSureMsg }}
                            </div>
                            <ul class="include-list">
                                <li>{{ optNavNameText }}</li>
                                <li>{{ optNavCompanyText }}</li>
                                <li>{{ optNavContentText }}</li>
                                <li>{{ optNavFrequentText }}</li>
                            </ul>
                        </div>
                        <div>
                            <ds-illustration class="card-illustration" name="completed-tasks-color" />
                        </div>
                    </div>
                </div>
                <div v-if="activeTab === REPLY_TAB" class="auto-reply-section">
                    <div class="settings-modal-title">
                        {{ autoNavHeaderText }}
                    </div>

                    <p class="description">
                        {{ autoNavReceiveMsg }}
                    </p>

                    <ds-text-area-field
                        ref="noreply"
                        v-model="noReplyMessage"
                        :label="autoNavNoReplyMsg"
                        :maxlength="SETTINGS_MAX_CHARACTER_COUNT + 1"
                        :invalid="hasNoReplyError"
                        submitted
                        class="message-input"
                        data-qa="no-reply-message"
                        required
                        auto-grow
                        @input="saveAutoReplyMessage"
                    >
                        <template v-if="activeTab === REPLY_TAB" #help>
                            <div class="character-count">
                                <ds-status
                                    v-if="!loadingAutoReply"
                                    class="input-status"
                                    emphasis="medium"
                                    type="good"
                                >
                                    {{ optNavSavedButton }}
                                </ds-status>
                                <ds-spinner v-else class="input-status" :size="12" />
                                {{ noReplyCharacterCount }}
                            </div>
                        </template>
                    </ds-text-area-field>

                    <sms-invalid-character-alert
                        v-if="isNoReplyMessageInvalid"
                        leading-icon
                        class="character-alert"
                        data-qa="encoding-alert"
                    />

                    <ds-inline-alert
                        v-if="hasNoReplyError"
                        leading-icon
                        class="character-alert"
                        type="critical"
                    >
                        Your message exceeds 306 characters and can no longer be saved. Please shorten.
                    </ds-inline-alert>

                    <div class="reply-tip-container">
                        <span class="subtitle">
                            {{ recommendedMsg }}
                        </span>
                    </div>

                    <p class="no-reply-tip">
                        {{ autoNavContactMsg }}
                    </p>
                </div>
            </section>
        </div>

        <ds-modal
            v-if="isOptinAlertOpen"
            dialog
            :is-open="isOptinAlertOpen"
            data-qa="sms-settings-confirm-close"
            @close="isOptinAlertOpen = false"
        >
            <span class="title settings-alert">You must include an opt-in message</span>

            <div class="description">
                Your opt-in message can't be blank. Please be sure to <strong> identify yourself or your company </strong> in the opt-in message.
            </div>

            <ds-text-button class="confirm-button" @click="isOptinAlertOpen = false">
                Got it
            </ds-text-button>
        </ds-modal>

        <ds-modal
            v-if="isReplyAlertOpen"
            dialog
            :is-open="isReplyAlertOpen"
            data-qa="sms-settings-confirm-noreply"
            @close="isReplyAlertOpen = false"
        >
            <span class="title settings-alert">You must include an auto-reply message</span>

            <div class="description">
                Your Keap Marketing Number can't yet accept replies, so <strong>your auto-reply message can't be blank</strong>. This message will be automatically sent to recipients who try to respond.
            </div>

            <ds-text-button class="confirm-button" @click="isReplyAlertOpen = false">
                Got it
            </ds-text-button>
        </ds-modal>
    </ds-modal>
</template>

<script>
import debounce from 'lodash.debounce';

import SmsTextUsage from './SmsTextUsage';
import SmsInvalidCharacterAlert from './SmsInvalidCharacterAlert';

import { INPUT_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';

import { formatPhone } from './phone.util';

import { MAX_CHARACTER_COUNT, SMS_ALLOWED_CHARS_REGEX } from './sms.constants';
import axios from 'axios';

const SETTINGS_MAX_CHARACTER_COUNT = 306;
const BILLING_TAB = 1;
const OPTIN_TAB = 2;
const REPLY_TAB = 3;


export default {
    MAX_CHARACTER_COUNT,

    components: {
        SmsTextUsage,
        SmsInvalidCharacterAlert,
    },

    props: {
        account: {
            type: Object,
            default: () => ({}),
        },
        isOpen: Boolean,
        isBillingChangesEnabled: Boolean,
    },

    data() {
        return {
            activeTab: 1,
            saving: false,
            saved: false,
            loadingOptin: false,
            loadingAutoReply: false,
            isOptinAlertOpen: false,
            isReplyAlertOpen: false,
            maxLength: MAX_CHARACTER_COUNT,
            SETTINGS_MAX_CHARACTER_COUNT,
            BILLING_TAB,
            OPTIN_TAB,
            REPLY_TAB,
            optInIntro: '',
            noReplyMessage: '',
            complianceLink: 'https://www.twilio.com/learn/call-and-text-marketing/guide-to-us-sms-compliance',
            bestPracticesLink: 'https://www.ctia.org/the-wireless-industry/industry-commitments/messaging-interoperability-sms-mms',
            tcpaLink: 'https://www.fdic.gov/regulations/compliance/manual/8/viii-5.1.pdf',
            headerText: '',
            numNavText: '',
            pricingTabText: '',
            optNavText: '',
            optInTitle: '',
            autoNavText: '',
            numNavHeader: '',
            numNavInfoMsg: '',
            numNavWarnMsg: '',
            numNavRuleOne: '',
            voiceAccountType: '',
            numNavRuleTwo: '',
            numNavCullalarTxt: '',
            numNavRuleThree: '',
            numNavTcpaText: '',
            numNavTextMsg: '',
            numNavHelpMsgLink: '',
            numNavHelpMsgText: '',
            optInText: 'Contacts that have not opted in will receive this message the first time you text them from your marketing number. They must reply YES to continue receiving your messages.',
            optNavReceiveMsg: '',
            optNavCustomMsg: '',
            optNavRequiredMsg: '',
            optNavSavedButton: '',
            optNavRecommendedMsg: '',
            optNavNotificationMsg: '',
            optNavSureMsg: '',
            optNavNameText: '',
            optNavCompanyText: '',
            optNavContentText: '',
            optNavFrequentText: '',
            autoNavHeaderText: '',
            autoNavReceiveMsg: '',
            autoNavNoReplyMsg: '',
            autoNavContactMsg: '',
            recommendedMsg: '',
            pricing: '',
            pricingDescriptionText: '',
            lawCustomMsg: '',
            costPerMessage: 'You\'ll only pay $0.015 per text received and sent to double opted-in recipients. Your subscription includes a certain amount of included texts per billing cycle. Once you\'ve used those, you\'ll begin getting charged for successfully sent and received messages. Your message count also includes long messages that are split into multiple segments.',
        };
    },

    created() {
        this.optInIntro = this.account?.optInMessageIntro;
        this.noReplyMessage = this.account?.replyMessage || 'This number doesn\'t accept replies. Please contact this business or person directly.';

        if (window.keapI18nMap !== undefined) {
            this.headerText = window.keapI18nMap['funnel.campaign.communications.textmsg.marketpopup.header'];
            this.numNavText = window.keapI18nMap['funnel.campaign.communications.textmsg.marketpopup.numnavtxt'];
            this.pricingTabText = window.keapI18nMap['funnel.campaign.communications.textmsg.marketpopup.pricingTabText'];
            this.optNavText = window.keapI18nMap['funnel.campaign.communications.textmsg.marketpopup.optnavtxt'];
            this.optInTitle = window.keapI18nMap['funnel.campaign.communications.textmsg.preview.doubleopt'];
            this.autoNavText = window.keapI18nMap['funnel.campaign.communications.textmsg.marketpopup.autonavtxt'];
            this.numNavHeader = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.header'];
            this.numNavInfoMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.infomsg'];
            this.numNavWarnMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.warnmsg'];
            this.numNavRuleOne = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.ruleone'];
            this.voiceAccountType = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.voiceaccttype'];
            this.numNavRuleTwo = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.ruletwo'];
            this.numNavCullalarTxt = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.cellulartext'];
            this.numNavRuleThree = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.rulethree'];
            this.numNavTcpaText = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.tcpatxt'];
            this.numNavHelpMsgLink = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.helpmsg.link'];
            this.numNavHelpMsgText = window.keapI18nMap['funnel.campaign.communications.marketpopup.numnav.helpmsg.text'];
            this.optNavReceiveMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.receivemsg'];
            this.optNavCustomMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.custommsg'];
            this.optNavRequiredMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.requiredmsg'];
            this.optNavSavedButton = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.saved.button'];
            this.optNavRecommendedMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.recommendedmsg'];
            this.optNavNotificationMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.notificationmsg'];
            this.optNavSureMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.suremsg'];
            this.optNavNameText = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.nametxt'];
            this.optNavCompanyText = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.companytxt'];
            this.optNavContentText = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.cotenttxt'];
            this.optNavFrequentText = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.frequenttxt'];
            this.autoNavHeaderText = window.keapI18nMap['funnel.campaign.communications.marketpopup.autonav.header'];
            this.autoNavReceiveMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.autonav.receivemsg'];
            this.autoNavNoReplyMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.autonav.noreplymsg'];
            this.autoNavContactMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.autonav.contactmsg'];
            this.recommendedMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.optnav.recommendedmsg'];
            this.pricing = window.keapI18nMap['funnel.campaign.communications.textmsg.marketpopup.pricing'];
            this.pricingDescriptionText = window.keapI18nMap['funnel.campaign.communications.textmsg.marketpopup.pricing.descif.text'];
            this.lawCustomMsg = window.keapI18nMap['funnel.campaign.communications.marketpopup.autonav.lawcustom.message'];
        } else {
            this.headerText = 'Marketing number settings';
            this.numNavText = 'Number and text message usage';
            this.pricingTabText = 'Number and pricing';
            this.optNavText = 'Opt-in request message';
            this.optInTitle = 'Double opt-in request message';
            this.autoNavText = 'Auto-reply message';
            this.numNavHeader = 'Your marketing number';
            this.numNavInfoMsg = 'All automated and broadcast messages are sent from this number.';
            this.numNavWarnMsg = 'Protect yourself by understanding the rules';
            this.numNavRuleOne = 'Guide to U.S. SMS Compliance';
            this.voiceAccountType = 'Twilio';
            this.numNavRuleTwo = 'Messaging Principles & Best Practices';
            this.numNavCullalarTxt = 'Cellular Telecommunications Industry Association';
            this.numNavRuleThree = 'Telephone Consumer Protection Act';
            this.numNavTcpaText = 'TCPA';
            this.numNavHelpMsgLink = 'How does pricing work?';
            this.numNavHelpMsgText = 'Each time an opted-in recipient receives your text message, you\'ll be charged $0.015. Your subscription includes a certain amount of included texts per billing cycle. Once you\'ve used those, you\'ll begin getting charged for successfully sent messages. Your message count also includes long messages that are split into multiple segments.';
            this.optNavReceiveMsg = 'Contacts receive this message the first time you text them from this number. They must reply YES to continue receiving your messages.';
            this.optNavCustomMsg = 'Customizable message';
            this.optNavRequiredMsg = 'Required message (can\'t be edited) *';
            this.optNavSavedButton = 'Saved';
            this.optNavRecommendedMsg = 'We recommend a message like this:';
            this.optNavNotificationMsg = 'Hi, it\'s Janine at Family Fitness. I\'d like to send you text messages from this number. You can expect occasional reminders, fitness tips, and notifications about your membership.';
            this.optNavSureMsg = 'Be sure to include:';
            this.optNavNameText = 'Your name';
            this.optNavCompanyText = 'Your company name';
            this.optNavContentText = 'The type of content you\'ll send';
            this.optNavFrequentText = 'How frequently you\'ll reach out';
            this.autoNavHeaderText = 'Auto-reply message';
            this.autoNavReceiveMsg = 'Contacts receive this message if they attempt to reply to your marketing number. Edit the message below to direct contacts elsewhere.';
            this.autoNavNoReplyMsg = 'No-reply message';
            this.autoNavContactMsg = 'Hi, it\'s Jenny at Fit Family. I\'m unable to reply from this number. Please contact me at (212) 466-7890, or send me an email at Jenny@fitfamily.com. Thanks!';
            this.recommendedMsg = 'We recommend a message like this:';
            this.pricing = 'Pricing';
            this.pricingDescriptionText = 'You\'ll only pay $0.015 per text sent to double opted-in recipients. Your subscription includes a certain amount of included texts per billing cycle. Once you\'ve used those, you\'ll begin getting charged for successfully sent messages. Your message count also includes long messages that are split into multiple segments.';
            this.lawCustomMsg = 'Your message exceeds 306 characters and can no longer be saved. Please shorten.';
        }
    },

    mounted() {
        this.saveOptInMessage = debounce(this.saveOptInMessage, INPUT_DEBOUNCE_DELAY);
        this.saveAutoReplyMessage = debounce(this.saveAutoReplyMessage, INPUT_DEBOUNCE_DELAY);
    },

    computed: {
        formattedFromNumber() {
            return formatPhone(this.account.phoneNumber);
        },

        displayAutoReply() {
            return !this.account.conversationsEnabled && this.account.autoReplyEnabled;
        },

        companyName() {
            return this.account.companyName;
        },

        optInCharacterCount() {
            return `${this.optInIntro.length + this.requiredInfo.length}/${this.SETTINGS_MAX_CHARACTER_COUNT}`;
        },

        noReplyCharacterCount() {
            return `${this.noReplyMessage.length}/${this.SETTINGS_MAX_CHARACTER_COUNT}`;
        },

        requiredInfo() {
            return this.account?.optInMessage;
        },

        showComplianceTip() {
            return !this.containsCompanyName;
        },

        hasOptinError() {
            return (this.optInIntro?.length + this.requiredInfo.length) > this.SETTINGS_MAX_CHARACTER_COUNT;
        },

        hasNoReplyError() {
            return this.noReplyMessage.length > this.SETTINGS_MAX_CHARACTER_COUNT;
        },

        containsCompanyName() {
            if (!this.optInIntro || !this.companyName) return false;

            return this.optInIntro.toLowerCase().includes(this.companyName.toLowerCase());
        },

        isOptinMessageInvalid() {
            return this.optInIntro && !SMS_ALLOWED_CHARS_REGEX.test(this.optInIntro.normalize());
        },

        isNoReplyMessageInvalid() {
            return this.noReplyMessage && !SMS_ALLOWED_CHARS_REGEX.test(this.noReplyMessage.normalize());
        },
    },

    methods: {
        close() {
            this.$emit('close');
        },

        handleTabClick(index) {
            if (this.activeTab === OPTIN_TAB && this.optInIntro.length < 1) {
                this.isOptinAlertOpen = true;
            } else if (this.activeTab === REPLY_TAB && this.noReplyMessage.length < 1) {
                this.isReplyAlertOpen = true;
            } else {
                this.activeTab = index;
            }
        },

        async saveOptInMessage() {
            if (this.optInIntro && !this.isOptinMessageInvalid && !this.hasOptinError) {
                this.loadingOptin = true;

                await axios.put('/app/automatedSmsFunnel/updateAccount', {
                    optInMessageIntro: this.optInIntro,
                });

                this.account.optInMessageIntro = this.optInIntro;
                this.$emit('update', this.account);

                this.loadingOptin = false;
            }
        },

        async saveAutoReplyMessage() {
            this.loadingAutoReply = true;

            if (this.noReplyMessage && !this.isNoReplyMessageInvalid && !this.hasNoReplyError) {
                await axios.put('/app/automatedSmsFunnel/updateAccount', {
                    replyMessage: this.noReplyMessage,
                });

                this.account.replyMessage = this.noReplyMessage;
                this.$emit('update', this.account);

                this.loadingAutoReply = false;
            }
        },

        isTabSelected(i) {
            return this.activeTab === i;
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
$tab-nav-width: #{px-to-rem(360px)};

.kmn-settings-modal {
    --modal-padding: 0;
}

.content-section {
    padding: $spacing-200 $spacing-400;
    border-left: 1px solid $color-ink-300;
}

.subtitle {
    font-weight: $font-weight-bold;
}

.request-button {
    display: flex;
    justify-content: center;
}

.include-list {
    list-style: unset;
    margin: $spacing-200 0 $spacing-300 $spacing-200;
    padding: 0;
    line-height: px-to-rem(20px);
}

.alert-title {
    font-weight: $font-weight-bold;
    font-size: $font-size-md;

    &.center {
        text-align: center;
    }
}

.card-content {
    padding: $spacing-200;
}

.tab-select {
    display: flex;
    min-height: px-to-rem(650);

    @media($small) {
        height: 100vh;
        flex-direction: column;
        justify-content: space-between;
    }
}

.compliance-alert {
    padding: $spacing-200 $spacing-600 $spacing-200 $spacing-200;
    margin: 0 0 $spacing-200;
}

.compliance-title {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
}

.collapsible-icon {
    --icon-color: #{$color-blue-900};
}

.free-period-description {
    font-style: italic;
    font-size: $font-size-sm;
    margin-top: $spacing-100;
}

.pricing-header {
    font-weight: $font-weight-bold;
    font-size: $font-size-lg;
    margin: $spacing-200 0 $spacing-100;
}

.pricing-title {
    font-weight: $font-weight-bold;
    font-size: $font-size-sm;
    text-transform: none;
    margin: $spacing-100 0 $spacing-100;
}

.pricing-description, .cost-per-message{
    font-size: $font-size-sm;
    line-height: $line-height-md;
}

.input-status {
    margin-right: $spacing-100;
}

.notification-text {
    margin-top: $spacing-100;
    text-align: center;
}

.character-alert {
    margin: $spacing-200 0;
    width: 100%;
}

.tip-card {
    display: flex;
    justify-content: space-between;
    background-color: $color-gray-050;
    border-radius: $border-radius;
    box-shadow: $elevation-z1;
    padding: $spacing-200 0 0 $spacing-200;
    width: 70%;
    margin-bottom: $spacing-600;

    &.request-card {
        width: 90%;
        padding: $spacing-400 0;
    }
}

.link-source {
    font-size: $font-size-xs;
    color: $color-text-subtle;
    line-height: 0.5rem;
}

.no-reply-tip {
    font-style: italic;
    margin-top: $spacing-200;
    padding-right: $spacing-600;
}

.card-illustration {
    padding-left: $spacing-100;
    padding-bottom: $spacing-500;
}

.compliance-link {
    margin-top: $spacing-100;
    font-size: $font-size-sm;
    color: $color-ink !important;
}

.tabs {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: $tab-nav-width;
    padding: 0;
    margin-top: 0;
    border-bottom: none;

    @media($small) {
        flex: 1;
        min-height: 0;
        max-width: 100%;
    }
}

.alert-icon {
    color: $color-text-subtle;
    margin: 0 $spacing-100 $spacing-050 0;
}

.settings-modal-title {
    font-weight: $font-weight-bold;
    font-size: $font-size-lg;
}

.description {
    font-size: $font-size-sm;
    color: $color-text-subtle;
    margin: 0 0 $spacing-200 0;
}

.message-input {
    --textarea-min-height: #{px-to-rem(32px)};
    --input-margin-bottom: #{$spacing-050};
}

.required-info {
    --textarea-min-height: #{px-to-rem(32px)};
    --input-margin-bottom: #{$spacing-050};
    --input-field-margin-top: #{$spacing-300};
}

.character-count {
    display: flex;
    justify-content: flex-end;
    font-size: $font-size-xs;
    align-items: center;

    &.error {
        color: $color-red;
    }
}

.confirm-button {
    width: 50%;
    align-self: flex-end;
    margin-top: $spacing-200;
}

.settings-alert {
    margin-bottom: $spacing-300;
}

.marketing-number {
    /*font-family: monospace;*/
    font-size: $font-size-lg;
    margin: $spacing-050 0;

}

.collapsible-tip {
    --collapsible-inner-padding: #{$spacing-050 $spacing-200 0 0};
    margin-bottom: $spacing-400;
}

.tab-items {
    @include overflowY;
}

.tab-item {
    cursor: pointer;

    --list-item-padding: #{$spacing-300};


    &.selected {
        background-color: $color-gray-100;
    }

    &:hover {
        background-color: $color-gray-050;
    }

    @media ($small) {
        &.selected {
            background-color: $color-paper;
        }
    }
}
</style>
